import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { loadTinyMce, TINYMCE_BASE } from '../utils/tinymceLoader.js';
import { PDF_PAGE, resolvePageBox } from '../utils/htmlToPdf.js';
import { SearchableSelect } from './SearchableSelect.jsx';

const PAGE_W = PDF_PAGE.widthPx;
const PAGE_H = PDF_PAGE.heightPx;
const PAGE_GAP_PX = 28;
const PAGE_ATTR = 'data-pdf-page';
const GAP_ATTR = 'data-pdf-page-gap';
const PAGE_EDGE_SLACK_PX = 2;

/**
 * Realm-safe element check. Nodes from TinyMCE's iframe fail
 * `instanceof HTMLElement` against the parent window's HTMLElement.
 * @param {Node|null|undefined} el
 */
function isHtmlEl(el) {
  return Boolean(el && el.nodeType === 1 && typeof el.getAttribute === 'function');
}

/**
 * appendChild that never throws HierarchyRequestError into the React overlay.
 * @param {Node|null|undefined} parent
 * @param {Node|null|undefined} child
 */
function safeAppendChild(parent, child) {
  if (!parent || !child || parent === child) return false;
  if (parent.nodeType !== 1 && parent.nodeType !== 9 && parent.nodeType !== 11) return false;
  try {
    if (typeof parent.contains === 'function' && child.nodeType === 1 && child.contains(parent)) {
      return false;
    }
    parent.appendChild(child);
    return true;
  } catch {
    return false;
  }
}

const SAFE_APPEND_KEY = '__portalPdfSafeAppendChild';

/**
 * TinyMCE Alloy (blocks/Heading toolbar) can call appendChild on a text node while
 * updating the format label — that throws HierarchyRequestError and trips the
 * webpack overlay. Patch the prototype so invalid calls are no-ops.
 * @param {Window|null|undefined} targetWindow
 * @returns {() => void}
 */
function installSafeAppendChildPatch(targetWindow) {
  const proto = targetWindow?.Node?.prototype;
  if (!proto || typeof proto.appendChild !== 'function') return () => {};
  if (proto[SAFE_APPEND_KEY]) return () => {};

  const original = proto.appendChild;
  proto[SAFE_APPEND_KEY] = original;
  proto.appendChild = function portalSafeAppendChild(child) {
    try {
      // Text / Comment / Attr cannot host children.
      if (this.nodeType !== 1 && this.nodeType !== 9 && this.nodeType !== 11) {
        return child;
      }
      if (child && child.nodeType === 1 && typeof child.contains === 'function' && child.contains(this)) {
        return child;
      }
      return original.call(this, child);
    } catch (err) {
      if (err && (err.name === 'HierarchyRequestError' || /HierarchyRequestError/i.test(String(err.message || '')))) {
        return child;
      }
      throw err;
    }
  };

  return () => {
    if (proto[SAFE_APPEND_KEY]) {
      proto.appendChild = proto[SAFE_APPEND_KEY];
      try {
        delete proto[SAFE_APPEND_KEY];
      } catch {
        proto[SAFE_APPEND_KEY] = undefined;
      }
    }
  };
}

/**
 * @param {Element|null|undefined} el
 */
function isPageShell(el) {
  return isHtmlEl(el) && el.getAttribute(PAGE_ATTR) != null;
}

/**
 * @param {Element|null|undefined} el
 */
function isPageGap(el) {
  return isHtmlEl(el) && el.getAttribute(GAP_ATTR) === '1';
}

/**
 * @param {Element|null|undefined} el
 */
function isManualPageBreak(el) {
  if (!isHtmlEl(el)) return false;
  if (el.classList?.contains('mce-pagebreak')) return true;
  if (el.getAttribute?.('data-mce-type') === 'pagebreak') return true;
  return false;
}

/**
 * @param {Element|null|undefined} el
 */
function isBookMark(el) {
  return isHtmlEl(el) && el.getAttribute('data-mce-type') === 'bookmark';
}

/**
 * @param {HTMLElement} el
 */
function blockHasMeaningfulContent(el) {
  if (isManualPageBreak(el) || isBookMark(el)) return false;
  const text = (el.textContent || '').replace(/\u00a0/g, ' ').trim();
  if (text.length > 0) return true;
  return Boolean(el.querySelector?.('img,table,video,iframe'));
}

/**
 * Always create nodes in the editor iframe document — parent-document nodes get
 * adopted unpredictably and TinyMCE often strips their children.
 * @param {Document} [doc]
 */
function editorDocument(doc) {
  return doc || (typeof document !== 'undefined' ? document : null);
}

/**
 * @param {ReturnType<typeof resolvePageBox>} box
 * @param {Document} [doc]
 */
function createPageShell(box, doc) {
  const d = editorDocument(doc);
  const page = d.createElement('div');
  page.setAttribute(PAGE_ATTR, '1');
  page.className = 'pdf-doc-page';
  lockPageChrome(page);
  page.style.setProperty('padding', `${box.top}px ${box.right}px ${box.bottom}px ${box.left}px`, 'important');
  page.style.setProperty('margin', '0', 'important');
  page.style.setProperty('background', '#ffffff', 'important');
  page.style.setProperty('border', '1px solid #b8bfc9', 'important');
  return page;
}

/**
 * @param {Document} [doc]
 */
function createPageGap(doc) {
  const d = editorDocument(doc);
  const gap = d.createElement('div');
  lockPageGap(gap);
  return gap;
}

/**
 * @param {import('tinymce').Editor} editor
 * @returns {HTMLElement | null}
 */
function getCaretBlock(editor) {
  try {
    const node = editor.selection.getNode();
    if (!node) return null;
    if (node.nodeType === 3) {
      return node.parentElement?.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote') || null;
    }
    if (isHtmlEl(node)) {
      return node.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote') || null;
    }
  } catch {
    // ignore
  }
  return null;
}

/**
 * Place caret inside a block that may have been moved across pages.
 * @param {import('tinymce').Editor} editor
 * @param {HTMLElement} block
 * @param {{ toStart?: boolean }} [opts]
 */
function placeCaretInBlock(editor, block, opts = {}) {
  if (!isHtmlEl(block)) return false;
  const body = editor.getBody?.();
  if (!body || !body.contains(block)) return false;
  try {
    const toStart = opts.toStart !== false;
    if (toStart) {
      editor.selection.setCursorLocation(block, 0);
    } else {
      const rng = editor.getDoc().createRange();
      rng.selectNodeContents(block);
      rng.collapse(false);
      editor.selection.setRng(rng);
    }
    return true;
  } catch {
    try {
      editor.selection.select(block, true);
      editor.selection.collapse(opts.toStart !== false);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * After Enter overflow: empty new line often lands on the next sheet while the caret
 * bookmark is restored to the end of the previous block. Jump the caret to that line.
 * @param {import('tinymce').Editor} editor
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function followEnterParagraphToNextPage(editor, box) {
  const body = editor.getBody?.();
  if (!body) return false;
  const block = getCaretBlock(editor);
  const page = block?.closest?.(`[${PAGE_ATTR}]`);
  if (!isHtmlEl(page) || !isHtmlEl(block)) return false;

  // Already on a later page with an empty/caret block — done.
  const next = (() => {
    let gap = page.nextElementSibling;
    let n = gap && isPageGap(gap) ? gap.nextElementSibling : gap;
    return n && isPageShell(n) ? /** @type {HTMLElement} */ (n) : null;
  })();
  if (!isHtmlEl(next)) return false;

  // Case A: caret block itself was moved to next page (live DOM node).
  if (next.contains(block)) {
    placeCaretInBlock(editor, block, { toStart: true });
    scrollCaretIntoView(editor, { prefer: 'start' });
    return true;
  }

  // Case B: caret stuck at end of page N, empty paragraph sits at start of page N+1.
  if (!isCaretAtEndOfPage(editor, page) && !pageNearBottom(page)) return false;
  const firstOnNext = Array.from(next.children).find(
    (c) => isHtmlEl(c) && !isBookMark(c) && !isManualPageBreak(c),
  );
  if (!isHtmlEl(firstOnNext)) return false;
  const emptyNext = !(firstOnNext.textContent || '').replace(/\u00a0/g, ' ').trim();
  if (!emptyNext && !pageNearBottom(page) && !pageOverflows(page)) return false;

  placeCaretInBlock(editor, firstOnNext, { toStart: true });
  scrollCaretIntoView(editor, { prefer: 'start' });
  return true;
}

/**
 * @param {import('tinymce').Editor} editor
 * @returns {unknown | null}
 */
function captureSelection(editor) {
  try {
    return editor.selection.getBookmark(2);
  } catch {
    return null;
  }
}

/**
 * @param {import('tinymce').Editor} editor
 * @param {unknown | null} bookmark
 */
function restoreSelection(editor, bookmark) {
  if (!bookmark) return;
  try {
    editor.selection.moveToBookmark(bookmark);
  } catch {
    // ignore
  }
}

/**
 * True while the user is dragging / has a text selection (must not reflow / collapse).
 * @param {import('tinymce').Editor} editor
 * @param {boolean} pointerSelecting
 * @param {number} [selectionLockUntil]
 */
function isUserSelecting(editor, pointerSelecting, selectionLockUntil = 0) {
  if (pointerSelecting) return true;
  if (selectionLockUntil && Date.now() < selectionLockUntil) return true;
  try {
    return !editor.selection.isCollapsed();
  } catch {
    // If selection APIs fail mid-gesture, assume selecting so we never reflow.
    return true;
  }
}

/**
 * Keep page shells locked to fixed A4 — never grow with content.
 * @param {HTMLElement} page
 */
function lockPageChrome(page) {
  page.classList.add('pdf-doc-page');
  page.setAttribute(PAGE_ATTR, page.getAttribute(PAGE_ATTR) || '1');
  page.style.setProperty('box-sizing', 'border-box', 'important');
  page.style.setProperty('width', `${PAGE_W}px`, 'important');
  page.style.setProperty('height', `${PAGE_H}px`, 'important');
  page.style.setProperty('min-height', `${PAGE_H}px`, 'important');
  page.style.setProperty('max-height', `${PAGE_H}px`, 'important');
  page.style.setProperty('overflow', 'hidden', 'important');
  page.style.setProperty('overflow-x', 'hidden', 'important');
  page.style.setProperty('overflow-y', 'hidden', 'important');
  page.style.setProperty('flex', 'none', 'important');
  page.scrollTop = 0;
  page.scrollLeft = 0;
}

/**
 * @param {HTMLElement} page
 * @param {{ relock?: boolean }} [opts]
 */
function pageOverflows(page, opts = {}) {
  const relock = opts.relock !== false;
  if (relock) lockPageChrome(page);
  void page.offsetHeight;
  // Content taller than the fixed A4 viewport (scrollHeight stays large with overflow:hidden).
  if (page.scrollHeight > PAGE_H + PAGE_EDGE_SLACK_PX) return true;
  if (page.scrollHeight > page.clientHeight + PAGE_EDGE_SLACK_PX) return true;
  return false;
}

/**
 * Absolute Y of the content floor inside an A4 page (above bottom padding/border).
 * @param {HTMLElement} page
 */
function pageContentFloorY(page) {
  lockPageChrome(page);
  const doc = page.ownerDocument;
  const view = doc.defaultView;
  if (!view) return 0;
  const pageRect = page.getBoundingClientRect();
  const style = view.getComputedStyle(page);
  const padBottom = Number.parseFloat(style.paddingBottom) || 0;
  const borderBottom = Number.parseFloat(style.borderBottomWidth) || 0;
  return pageRect.bottom - padBottom - borderBottom;
}

/**
 * Free space (px) below the last content block inside the page.
 * @param {HTMLElement} page
 */
function pageRoomBelowLastBlock(page) {
  lockPageChrome(page);
  const last = Array.from(page.children).reverse().find(
    (c) => isHtmlEl(c) && !isBookMark(c) && !isManualPageBreak(c),
  );
  if (!isHtmlEl(last)) return PAGE_H;
  const floor = pageContentFloorY(page);
  return floor - last.getBoundingClientRect().bottom;
}

/**
 * True when the last block already sits on / past the bottom margin of the A4 sheet
 * (or there isn't room for another line).
 * @param {HTMLElement} page
 */
function pageNearBottom(page) {
  lockPageChrome(page);
  if (pageOverflows(page)) return true;
  // ~1.5 lines — Enter should jump to the next sheet instead of clipping a new <p>.
  return pageRoomBelowLastBlock(page) <= 36;
}

/**
 * Caret is at the very end of the page's last block (Enter should go to next sheet).
 * @param {import('tinymce').Editor} editor
 * @param {HTMLElement} page
 */
function isCaretAtEndOfPage(editor, page) {
  let rng;
  try {
    rng = editor.selection.getRng();
  } catch {
    return false;
  }
  if (!rng || !rng.collapsed) return false;
  const last = Array.from(page.children).reverse().find(
    (c) => isHtmlEl(c) && !isBookMark(c) && !isManualPageBreak(c),
  );
  if (!isHtmlEl(last)) return false;
  let block = null;
  try {
    const node = editor.selection.getNode();
    block = node?.nodeType === 3
      ? node.parentElement?.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote')
      : node?.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote');
  } catch {
    block = null;
  }
  if (!isHtmlEl(block) || !page.contains(block)) return false;
  if (block !== last && !last.contains(block)) return false;

  const doc = page.ownerDocument;
  try {
    const end = doc.createRange();
    end.selectNodeContents(last);
    end.collapse(false);
    return rng.compareBoundaryPoints(Range.START_TO_START, end) >= 0;
  } catch {
    // Empty / BR-only last block — caret is effectively at end.
    const text = (last.textContent || '').replace(/\u00a0/g, ' ').trim();
    return text.length === 0;
  }
}

/**
 * Caret's block is clipped under the A4 floor (new Enter line stuck on full page).
 * @param {HTMLElement} block
 * @param {HTMLElement} page
 */
function isBlockClippedOnPage(block, page) {
  if (!isHtmlEl(block) || !isHtmlEl(page) || !page.contains(block)) return false;
  const floor = pageContentFloorY(page);
  const rect = block.getBoundingClientRect();
  // Starts at/under the floor, or mostly below it.
  if (rect.top >= floor - 2) return true;
  if (rect.bottom > floor + 4 && pageOverflows(page)) {
    const last = Array.from(page.children).reverse().find(
      (c) => isHtmlEl(c) && !isBookMark(c) && !isManualPageBreak(c),
    );
    return last === block || last?.contains?.(block);
  }
  return false;
}

/**
 * @param {HTMLElement} el
 */
function isSplittableListOrTable(el) {
  const tag = el.tagName;
  return (tag === 'UL' || tag === 'OL' || tag === 'TABLE') && el.children.length > 1;
}

/**
 * @param {HTMLElement} el
 */
function isTextLikeBlock(el) {
  return /^(P|H1|H2|H3|H4|BLOCKQUOTE)$/.test(el.tagName);
}

/**
 * Split an overflowing text block so the page no longer scrolls/clips mid-content.
 * @param {HTMLElement} page
 * @param {HTMLElement} el
 * @param {HTMLElement} body
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function splitOverflowingTextBlock(page, el, body, box) {
  if (!isTextLikeBlock(el) || !pageOverflows(page)) return false;
  const doc = page.ownerDocument;
  const view = doc.defaultView;
  if (!view) return false;

  const pageRect = page.getBoundingClientRect();
  const style = view.getComputedStyle(page);
  const padBottom = Number.parseFloat(style.paddingBottom) || 0;
  const borderBottom = Number.parseFloat(style.borderBottomWidth) || 0;
  const floorAbs = pageRect.bottom - padBottom - borderBottom - PAGE_EDGE_SLACK_PX;

  const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  /** @type {Text | null} */
  let splitNode = null;
  let splitOffset = 0;
  /** @type {Text | null} */
  let node = /** @type {Text | null} */ (walker.nextNode());
  while (node) {
    const text = node.textContent || '';
    if (!text.length) {
      node = /** @type {Text | null} */ (walker.nextNode());
      continue;
    }
    const full = doc.createRange();
    full.setStart(node, 0);
    full.setEnd(node, text.length);
    if (full.getBoundingClientRect().bottom <= floorAbs + 0.5) {
      node = /** @type {Text | null} */ (walker.nextNode());
      continue;
    }
    const probe = doc.createRange();
    probe.setStart(node, 0);
    probe.setEnd(node, Math.min(1, text.length));
    if (probe.getBoundingClientRect().top >= floorAbs - 0.5) {
      splitNode = node;
      splitOffset = 0;
      break;
    }
    let lo = 0;
    let hi = text.length;
    while (lo < hi) {
      const mid = Math.ceil((lo + hi) / 2);
      probe.setStart(node, 0);
      probe.setEnd(node, mid);
      if (probe.getBoundingClientRect().bottom > floorAbs) hi = mid - 1;
      else lo = mid;
    }
    let offset = Math.max(0, lo);
    const slice = text.slice(0, offset + 1);
    const ws = Math.max(slice.lastIndexOf(' '), slice.lastIndexOf('\n'), slice.lastIndexOf('\t'));
    if (ws >= Math.floor(offset * 0.5)) offset = ws + 1;
    splitNode = node;
    splitOffset = Math.min(text.length, Math.max(0, offset));
    break;
  }
  if (!splitNode) return false;

  const pre = doc.createRange();
  pre.setStart(el, 0);
  pre.setEnd(splitNode, splitOffset);
  if (!String(pre.toString() || '').trim()) return false;

  const second = /** @type {HTMLElement} */ (el.cloneNode(false));
  const tail = doc.createRange();
  tail.setStart(splitNode, splitOffset);
  tail.setEndAfter(el.lastChild || splitNode);
  try {
    second.appendChild(tail.extractContents());
  } catch {
    return false;
  }
  if (!String(second.textContent || '').trim()) {
    while (second.firstChild) {
      if (!safeAppendChild(el, second.firstChild)) break;
    }
    return false;
  }

  const nextPage = getOrCreateNextPage(page, body, box);
  nextPage.insertBefore(second, nextPage.firstChild);
  lockPageChrome(page);
  lockPageChrome(nextPage);
  return true;
}

/**
 * Fit images inside a page shell so they do not sit clipped forever.
 * @param {HTMLElement} root
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function fitMedia(root, box) {
  const maxH = Math.max(120, box.contentHeight - 8);
  root.querySelectorAll('img').forEach((img) => {
    if (!(img instanceof HTMLImageElement)) return;
    img.style.maxWidth = '100%';
    img.style.maxHeight = `${maxH}px`;
    img.style.height = 'auto';
    img.style.objectFit = 'contain';
    if (img.hasAttribute('height')) img.removeAttribute('height');
  });
}

/**
 * Enter often splits a page shell into two adjacent page divs (no gray gap).
 * - If the first sheet is already full → keep both pages and insert a gap (Word-like page break).
 * - Otherwise merge (mid-page split) and let overflow rebalance refill A4 pages.
 * @param {HTMLElement} body
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function healAdjacentPageShells(body, box) {
  let guard = 0;
  while (guard < 40) {
    guard += 1;
    const kids = Array.from(body.children).filter((c) => isHtmlEl(c));
    let changed = false;
    for (let i = 0; i < kids.length - 1; i += 1) {
      const a = kids[i];
      const b = kids[i + 1];
      if (!isPageShell(a) || !isPageShell(b)) continue;

      lockPageChrome(a);
      lockPageChrome(b);
      a.style.setProperty('padding', `${box.top}px ${box.right}px ${box.bottom}px ${box.left}px`, 'important');
      b.style.setProperty('padding', `${box.top}px ${box.right}px ${box.bottom}px ${box.left}px`, 'important');

      // First sheet already fills A4 — Enter at the bottom should start page 2, not grow page 1.
      // Also keep the split when page 1 has no room for another line (avoids merge→clip loop).
      if (pageNearBottom(a) || pageOverflows(a) || pageRoomBelowLastBlock(a) <= 40) {
        const gap = createPageGap(body.ownerDocument);
        body.insertBefore(gap, b);
        lockPageChrome(a);
        lockPageChrome(b);
        changed = true;
        break;
      }

      // Mid-page accidental shell split — merge, then overflow packer will re-split if needed.
      while (b.firstChild) {
        if (!safeAppendChild(a, b.firstChild)) break;
      }
      b.remove();
      lockPageChrome(a);
      changed = true;
      break;
    }
    if (!changed) break;
  }
}

/**
 * @param {HTMLElement} body
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function ensureAtLeastOnePage(body, box) {
  const existing = Array.from(body.children).filter(isPageShell);
  if (existing.length) return existing[0];

  const doc = body.ownerDocument;
  const page = createPageShell(box, doc);
  const strays = Array.from(body.children);
  safeAppendChild(body, page);
  for (const node of strays) {
    if (node !== page && !isPageGap(node)) safeAppendChild(page, node);
  }
  if (!page.childElementCount) {
    const p = doc.createElement('p');
    p.innerHTML = '<br data-mce-bogus="1">';
    safeAppendChild(page, p);
  }
  return page;
}

/**
 * @param {import('tinymce').Editor} editor
 * @returns {HTMLElement | null}
 */
function selectionHostPage(editor) {
  try {
    const node = editor.selection.getNode();
    if (!node) return null;
    const el = node.nodeType === 3 ? node.parentElement : node;
    const host = el?.closest?.(`[${PAGE_ATTR}]`);
    return isHtmlEl(host) ? /** @type {HTMLElement} */ (host) : null;
  } catch {
    return null;
  }
}

/**
 * Click on the gray desk / page gap → pick the page (and caret edge) like a PDF viewer.
 * @param {HTMLElement} body
 * @param {number} clientY
 * @returns {{ page: HTMLElement | null, toStart: boolean }}
 */
function resolvePageForDeskClick(body, clientY) {
  const pages = Array.from(body.children).filter(isPageShell);
  if (!pages.length) return { page: null, toStart: false };

  for (let i = 0; i < pages.length; i += 1) {
    const page = /** @type {HTMLElement} */ (pages[i]);
    const rect = page.getBoundingClientRect();
    if (clientY >= rect.top && clientY <= rect.bottom) {
      return { page, toStart: clientY < (rect.top + rect.bottom) / 2 };
    }
    const next = pages[i + 1];
    if (!next) continue;
    const nextRect = next.getBoundingClientRect();
    if (clientY > rect.bottom && clientY < nextRect.top) {
      const mid = (rect.bottom + nextRect.top) / 2;
      if (clientY < mid) return { page, toStart: false };
      return { page: /** @type {HTMLElement} */ (next), toStart: true };
    }
  }

  if (clientY < pages[0].getBoundingClientRect().top) {
    return { page: /** @type {HTMLElement} */ (pages[0]), toStart: true };
  }
  return { page: /** @type {HTMLElement} */ (pages[pages.length - 1]), toStart: false };
}

/**
 * Writable block at the start/end of a page shell (creates an empty <p> if needed).
 * @param {HTMLElement} page
 * @param {{ toStart?: boolean }} [opts]
 */
function writableBlockOnPage(page, opts = {}) {
  const toStart = opts.toStart === true;
  const kids = Array.from(page.children).filter(
    (c) => isHtmlEl(c) && !isBookMark(c) && !isManualPageBreak(c),
  );
  if (kids.length) {
    return /** @type {HTMLElement} */ (toStart ? kids[0] : kids[kids.length - 1]);
  }
  const doc = page.ownerDocument;
  const p = doc.createElement('p');
  p.innerHTML = '<br data-mce-bogus="1">';
  page.appendChild(p);
  return p;
}

/**
 * Keep the caret/selection inside an A4 page — never on the gray desk or in a gap.
 * @param {import('tinymce').Editor} editor
 * @param {{ clientY?: number, force?: boolean }} [opts]
 */
function constrainCaretToPages(editor, opts = {}) {
  const body = editor.getBody?.();
  if (!body) return false;
  if (!opts.force && selectionHostPage(editor)) return false;

  const pageList = Array.from(body.children).filter(isPageShell);
  const { page, toStart } = typeof opts.clientY === 'number'
    ? resolvePageForDeskClick(body, opts.clientY)
    : { page: /** @type {HTMLElement | null} */ (pageList[pageList.length - 1] || null), toStart: false };
  if (!isHtmlEl(page)) return false;

  const block = writableBlockOnPage(page, { toStart });
  return placeCaretInBlock(editor, block, { toStart });
}

/**
 * Relock gap chrome so TinyMCE cannot turn the desk into an editable strip.
 * @param {HTMLElement} gap
 */
function lockPageGap(gap) {
  if (!isHtmlEl(gap)) return;
  gap.setAttribute(GAP_ATTR, '1');
  gap.setAttribute('contenteditable', 'false');
  gap.setAttribute('data-mce-contenteditable', 'false');
  gap.classList.add('pdf-doc-page-gap');
  gap.style.cssText = [
    `box-sizing:border-box`,
    `width:${PAGE_W}px`,
    `height:${PAGE_GAP_PX}px`,
    'overflow:hidden',
    'margin:0',
    'padding:0',
    'border:0',
    'line-height:0',
    'font-size:0',
    'background:#c5cad3',
    'pointer-events:none',
    'user-select:none',
  ].join(';');
  // Gaps are chrome only — never keep typed content between sheets.
  if (gap.innerHTML !== '&nbsp;') gap.innerHTML = '&nbsp;';
}

/**
 * Scoop anything that escaped onto the gray desk (body-level text / <p> between sheets)
 * back into the nearest page. Text nodes used to be skipped — that let users type in the gap.
 * @param {import('tinymce').Editor} editor
 */
function rehomeStrayBodyNodes(editor) {
  const body = editor.getBody?.();
  if (!body) return;
  const pages = Array.from(body.children).filter(isPageShell);
  if (!pages.length) return;
  const doc = body.ownerDocument;

  /** @param {Node} node */
  const pageBeforeNode = (node) => {
    let prev = node.previousSibling;
    while (prev) {
      if (isPageShell(prev)) return /** @type {HTMLElement} */ (prev);
      if (isPageGap(prev) && isPageShell(prev.previousElementSibling)) {
        return /** @type {HTMLElement} */ (prev.previousElementSibling);
      }
      prev = prev.previousSibling;
    }
    return /** @type {HTMLElement} */ (pages[0]);
  };

  for (const child of Array.from(body.childNodes)) {
    if (isPageShell(child)) continue;

    if (isPageGap(child)) {
      // If a gap somehow gained real content, pull it onto the previous page first.
      const meaningful = Array.from(child.childNodes).some((n) => {
        if (n.nodeType === 3) return Boolean((n.textContent || '').replace(/\u00a0/g, ' ').trim());
        if (!isHtmlEl(n)) return false;
        return blockHasMeaningfulContent(/** @type {HTMLElement} */ (n));
      });
      if (meaningful) {
        const home = pageBeforeNode(child);
        for (const n of Array.from(child.childNodes)) {
          if (n.nodeType === 3) {
            const text = (n.textContent || '').replace(/\u00a0/g, ' ').trim();
            if (!text) continue;
            const p = doc.createElement('p');
            p.textContent = n.textContent || '';
            safeAppendChild(home, p);
          } else if (isHtmlEl(n) && !isBookMark(n)) {
            safeAppendChild(home, n);
          }
        }
      }
      lockPageGap(/** @type {HTMLElement} */ (child));
      continue;
    }

    const home = pageBeforeNode(child);

    if (child.nodeType === 3) {
      const text = (child.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (!text) {
        child.parentNode?.removeChild(child);
        continue;
      }
      const p = doc.createElement('p');
      safeAppendChild(p, child);
      safeAppendChild(home, p);
      continue;
    }

    if (!isHtmlEl(child)) {
      child.parentNode?.removeChild(child);
      continue;
    }

    // Never move TinyMCE selection bookmarks / caret helpers across pages mid-gesture.
    if (isBookMark(child) || child.getAttribute?.('data-mce-bogus')) continue;

    safeAppendChild(home, child);
  }
}

/**
 * @param {HTMLElement} page
 * @param {HTMLElement} body
 * @param {ReturnType<typeof resolvePageBox>} box
 * @returns {HTMLElement}
 */
function getOrCreateNextPage(page, body, box) {
  let gap = page.nextElementSibling;
  let next = gap && isPageGap(gap) ? gap.nextElementSibling : gap;
  if (next && isPageShell(next)) return /** @type {HTMLElement} */ (next);

  const doc = body.ownerDocument;
  if (!gap || !isPageGap(gap)) {
    gap = createPageGap(doc);
    if (page.nextSibling) body.insertBefore(gap, page.nextSibling);
    else body.appendChild(gap);
  }
  next = createPageShell(box, doc);
  if (gap.nextSibling) body.insertBefore(next, gap.nextSibling);
  else body.appendChild(next);
  return next;
}

/**
 * Move overflowing blocks from `page` onto following pages (incremental, no full rebuild).
 * @param {HTMLElement} page
 * @param {HTMLElement} body
 * @param {ReturnType<typeof resolvePageBox>} box
 * @param {number} guard
 */
function pushOverflowForward(page, body, box, guard = 0) {
  lockPageChrome(page);
  if (guard > 120 || !pageOverflows(page)) return;

  fitMedia(page, box);
  void page.offsetHeight;
  lockPageChrome(page);
  if (!pageOverflows(page)) return;

  const blocks = Array.from(page.children).filter((c) => isHtmlEl(c) && !isBookMark(c));
  if (!blocks.length) return;

  // Single oversized block — split text/lists/tables so the page never gains an inner scrollbar.
  if (blocks.length === 1) {
    const only = blocks[0];
    // Unwrap generic wrappers so we can move real blocks across pages.
    if (
      only.tagName === 'DIV'
      && !isManualPageBreak(only)
      && !only.classList.contains('mce-pagebreak')
      && only.children.length > 0
    ) {
      while (only.firstChild) page.insertBefore(only.firstChild, only);
      only.remove();
      pushOverflowForward(page, body, box, guard + 1);
      return;
    }
    if (isSplittableListOrTable(only) && only.children.length > 1) {
      const nextPage = getOrCreateNextPage(page, body, box);
      const second = /** @type {HTMLElement} */ (only.cloneNode(false));
      while (only.children.length > 1 && pageOverflows(page)) {
        const kid = only.lastElementChild;
        if (!isHtmlEl(kid)) break;
        second.insertBefore(kid, second.firstChild);
        void page.offsetHeight;
      }
      if (second.childNodes.length) {
        nextPage.insertBefore(second, nextPage.firstChild);
        lockPageChrome(nextPage);
        pushOverflowForward(nextPage, body, box, guard + 1);
      }
      lockPageChrome(page);
      return;
    }
    if (isTextLikeBlock(only) && splitOverflowingTextBlock(page, only, body, box)) {
      pushOverflowForward(page, body, box, guard + 1);
      const next = page.nextElementSibling && isPageGap(page.nextElementSibling)
        ? page.nextElementSibling.nextElementSibling
        : page.nextElementSibling;
      if (next && isPageShell(next)) pushOverflowForward(next, body, box, guard + 1);
      return;
    }
    lockPageChrome(page);
    return;
  }

  const last = blocks[blocks.length - 1];

  // Prefer splitting lists/tables instead of moving the whole block when possible.
  if (isSplittableListOrTable(last) && last.children.length > 1) {
    /** @type {HTMLElement[]} */
    const moved = [];
    while (last.children.length > 1 && pageOverflows(page)) {
      const kid = last.lastElementChild;
      if (!isHtmlEl(kid)) break;
      moved.unshift(kid);
      kid.remove();
    }
    if (moved.length && !pageOverflows(page)) {
      const nextPage = getOrCreateNextPage(page, body, box);
      const second = /** @type {HTMLElement} */ (last.cloneNode(false));
      for (const kid of moved) safeAppendChild(second, kid);
      nextPage.insertBefore(second, nextPage.firstChild);
      lockPageChrome(page);
      lockPageChrome(nextPage);
      pushOverflowForward(nextPage, body, box, guard + 1);
      return;
    }
    for (const kid of moved) safeAppendChild(last, kid);
  }

  if (isTextLikeBlock(last) && splitOverflowingTextBlock(page, last, body, box)) {
    pushOverflowForward(page, body, box, guard + 1);
    return;
  }

  const nextPage = getOrCreateNextPage(page, body, box);
  nextPage.insertBefore(last, nextPage.firstChild);
  lockPageChrome(page);
  lockPageChrome(nextPage);

  if (pageOverflows(page)) {
    pushOverflowForward(page, body, box, guard + 1);
  }
  pushOverflowForward(nextPage, body, box, guard + 1);
}

/**
 * Remove trailing empty pages (keep at least one).
 * @param {HTMLElement} body
 */
function trimTrailingEmptyPages(body) {
  const pages = Array.from(body.querySelectorAll(`[${PAGE_ATTR}]`));
  for (let i = pages.length - 1; i > 0; i -= 1) {
    const pg = pages[i];
    const meaningful = Array.from(pg.children).some(
      (c) => isHtmlEl(c) && blockHasMeaningfulContent(c),
    );
    if (meaningful) break;
    const gap = pg.previousElementSibling;
    pg.remove();
    if (gap && isPageGap(gap)) gap.remove();
  }
}

/**
 * Full pack — only for initial load / margin changes / SetContent.
 * Builds page shells via native DOM (avoids TinyMCE stripping children from divs).
 * @param {import('tinymce').Editor} editor
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function rebuildAllPages(editor, box) {
  const body = editor.getBody?.();
  if (!body) return;
  const doc = body.ownerDocument;

  // Capture flat HTML BEFORE touching the live DOM.
  const flatHtml = serializeLiveDocument(editor);
  const source = doc.createElement('div');
  source.innerHTML = flatHtml || '<p></p>';

  /** @type {HTMLElement[]} */
  const blocks = Array.from(source.children).filter((c) => isHtmlEl(c));
  if (!blocks.length) {
    body.innerHTML = '';
    const page = createPageShell(box, doc);
    const p = doc.createElement('p');
    p.innerHTML = '<br data-mce-bogus="1">';
    page.appendChild(p);
    body.appendChild(page);
    return;
  }

  // Off-DOM packer in the iframe document (same CSS / metrics as live pages).
  const packer = doc.createElement('div');
  packer.style.cssText = [
    'position:absolute',
    'left:-10000px',
    'top:0',
    `width:${PAGE_W}px`,
    'visibility:hidden',
    'pointer-events:none',
  ].join(';');
  doc.documentElement.appendChild(packer);

  /** @type {string[]} */
  const pageHtmlChunks = [];
  let page = createPageShell(box, doc);
  packer.appendChild(page);

  const commitPage = () => {
    pageHtmlChunks.push(page.outerHTML);
  };

  const startNewPage = () => {
    commitPage();
    pageHtmlChunks.push(createPageGap(doc).outerHTML);
    page = createPageShell(box, doc);
    packer.replaceChildren(page);
  };

  for (const block of blocks) {
    if (isManualPageBreak(block)) {
      if (Array.from(page.children).some((c) => isHtmlEl(c) && blockHasMeaningfulContent(c))) {
        startNewPage();
      }
      const marker = doc.createElement('div');
      marker.className = 'mce-pagebreak pdf-manual-page-break';
      marker.setAttribute('data-mce-type', 'pagebreak');
      marker.setAttribute('contenteditable', 'false');
      marker.setAttribute('data-mce-contenteditable', 'false');
      marker.style.cssText = 'height:0;margin:0;padding:0;overflow:hidden;border:0;';
      page.appendChild(marker);
      continue;
    }

    page.appendChild(block);
    fitMedia(page, box);
    void page.offsetHeight;
    if (pageOverflows(page) && page.children.length > 1) {
      page.removeChild(block);
      startNewPage();
      page.appendChild(block);
      fitMedia(page, box);
    }
  }

  if (!page.childElementCount) {
    const p = doc.createElement('p');
    p.innerHTML = '<br data-mce-bogus="1">';
    page.appendChild(p);
  }
  commitPage();
  packer.remove();

  // Write shells in one shot; TinyMCE often strips children if we appendChild live.
  body.innerHTML = pageHtmlChunks.join('') || createPageShell(box, doc).outerHTML;

  // Second pass on live DOM: push remaining overflow forward.
  for (const pg of Array.from(body.querySelectorAll(`[${PAGE_ATTR}]`))) {
    if (isHtmlEl(pg)) {
      lockPageChrome(pg);
      fitMedia(pg, box);
      pushOverflowForward(pg, body, box);
      lockPageChrome(pg);
    }
  }
  trimTrailingEmptyPages(body);

  // Safety: if packing wiped everything, restore flat content into one page via innerHTML
  // (same document) so TinyMCE cannot drop cross-document children.
  const restored = serializeLiveDocument(editor);
  const restoredText = restored.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const sourceText = flatHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (sourceText.length > 20 && restoredText.length < Math.min(20, sourceText.length / 5)) {
    const fallback = createPageShell(box, doc);
    fallback.innerHTML = flatHtml;
    body.innerHTML = fallback.outerHTML;
    const still = serializeLiveDocument(editor).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (still.length >= 8) {
      const live = body.querySelector(`[${PAGE_ATTR}]`);
      if (isHtmlEl(live)) fitMedia(live, box);
    }
  }
}

/**
 * Cheap incremental sync used while typing — only pushes overflow forward.
 * @param {import('tinymce').Editor} editor
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function balanceOverflow(editor, box) {
  const body = editor.getBody?.();
  if (!body) return;

  healAdjacentPageShells(body, box);
  rehomeStrayBodyNodes(editor);
  ensureAtLeastOnePage(body, box);

  // Always walk from the first page — Enter at the end of page 1 must reflow page 1→2.
  const pages = Array.from(body.querySelectorAll(`[${PAGE_ATTR}]`)).filter(
    (n) => isHtmlEl(n),
  );

  for (const page of pages) {
    lockPageChrome(page);
    page.style.setProperty('padding', `${box.top}px ${box.right}px ${box.bottom}px ${box.left}px`, 'important');
    fitMedia(page, box);
    if (pageOverflows(page)) pushOverflowForward(page, body, box);
    lockPageChrome(page);
  }
  trimTrailingEmptyPages(body);
  body.querySelectorAll(`[${PAGE_ATTR}]`).forEach((pg) => {
    if (isHtmlEl(pg)) {
      lockPageChrome(pg);
      pg.style.setProperty('padding', `${box.top}px ${box.right}px ${box.bottom}px ${box.left}px`, 'important');
    }
  });
  body.querySelectorAll(`[${GAP_ATTR}]`).forEach((gap) => {
    if (isHtmlEl(gap)) lockPageGap(gap);
  });
  try {
    if (editor.selection.isCollapsed()) constrainCaretToPages(editor);
  } catch {
    // ignore — never disturb an active selection after reflow
  }
}

/**
 * After Enter/overflow, keep the caret on a writable block (not clipped under A4 bottom).
 * @param {import('tinymce').Editor} editor
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function ensureCaretOnWritablePage(editor, box) {
  const body = editor.getBody?.();
  if (!body) return;
  if (!selectionHostPage(editor)) {
    constrainCaretToPages(editor, { force: true });
  }
  let node;
  try {
    node = editor.selection.getNode();
  } catch {
    return;
  }
  let block = null;
  if (node?.nodeType === 3) {
    block = node.parentElement?.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote');
  } else if (isHtmlEl(node)) {
    block = node.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote') || null;
  }
  const page = (block || node)?.closest?.(`[${PAGE_ATTR}]`);
  if (!isHtmlEl(page)) return;
  lockPageChrome(page);
  if (pageOverflows(page)) {
    pushOverflowForward(page, body, box);
  }

  // Re-resolve after overflow push — DOM nodes may have moved to the next sheet.
  try {
    node = editor.selection.getNode();
    block = node?.nodeType === 3
      ? node.parentElement?.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote')
      : node?.closest?.('p,h1,h2,h3,h4,li,td,th,blockquote');
  } catch {
    block = null;
  }
  let host = block?.closest?.(`[${PAGE_ATTR}]`);
  if (isHtmlEl(block) && isHtmlEl(host) && isBlockClippedOnPage(block, host)) {
    const nextPage = getOrCreateNextPage(host, body, box);
    // Move the clipped caret block onto the next sheet and place the caret there.
    if (block.parentElement === host) {
      nextPage.insertBefore(block, nextPage.firstChild);
    } else if (!nextPage.contains(block)) {
      safeAppendChild(nextPage, block);
    }
    lockPageChrome(host);
    lockPageChrome(nextPage);
    try {
      const atStart = !(block.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (atStart) editor.selection.setCursorLocation(block, 0);
      else {
        const rng = editor.getDoc().createRange();
        rng.selectNodeContents(block);
        rng.collapse(true);
        editor.selection.setRng(rng);
      }
    } catch {
      try {
        editor.selection.setCursorLocation(block, 0);
      } catch {
        // ignore
      }
    }
    host = nextPage;
  }

  if (isHtmlEl(host)) lockPageChrome(host);
  scrollCaretIntoView(editor, { prefer: isHtmlEl(host) && host !== page ? 'start' : 'nearest' });
}

/**
 * Scroll the iframe so the caret block stays visible.
 * Must NOT scroll `[data-pdf-page]` itself — sheets are taller than the viewport, and
 * `scrollIntoView({block:'nearest'})` then pins the page bottom to the viewport bottom.
 * @param {import('tinymce').Editor} editor
 * @param {{ prefer?: 'nearest' | 'start' }} [opts]
 */
function scrollCaretIntoView(editor, opts = {}) {
  const win = editor.getWin?.();
  if (!win) return;

  let block = null;
  try {
    const node = editor.selection.getNode();
    if (!node) return;
    if (node.nodeType === 3) {
      block = node.parentElement;
    } else if (isHtmlEl(node)) {
      block = node.closest?.('p,h1,h2,h3,h4,h5,h6,li,td,th,blockquote,pre') || node;
    }
  } catch {
    return;
  }
  if (!isHtmlEl(block) || isPageShell(block) || isPageGap(block)) return;

  const preferStart = opts.prefer === 'start';
  try {
    block.scrollIntoView({
      block: preferStart ? 'start' : 'nearest',
      inline: 'nearest',
      behavior: 'auto',
    });
  } catch {
    try {
      block.scrollIntoView(preferStart);
    } catch {
      // ignore
    }
  }

  // Nudge if zoom/layout left the caret outside the visible iframe.
  try {
    const rect = block.getBoundingClientRect();
    const viewH = win.innerHeight || 0;
    if (viewH <= 0) return;
    const pad = Math.min(48, Math.floor(viewH * 0.12));
    if (rect.top < pad) {
      win.scrollBy(0, rect.top - pad);
    } else if (rect.bottom > viewH - pad) {
      win.scrollBy(0, rect.bottom - (viewH - pad));
    }
  } catch {
    // ignore
  }
}

/**
 * Serialize live DOM without going through TinyMCE getContent (avoids heavy work with images).
 * @param {import('tinymce').Editor} editor
 */
function serializeLiveDocument(editor) {
  const body = editor.getBody?.();
  if (!body) return '<p></p>';

  /** @type {string[]} */
  const parts = [];
  const pushBreak = () => {
    parts.push('<div class="mce-pagebreak pdf-manual-page-break" data-mce-type="pagebreak"></div>');
  };

  for (const child of Array.from(body.children)) {
    if (!isHtmlEl(child)) continue;
    if (isPageGap(child)) continue;

    if (isPageShell(child)) {
      for (const inner of Array.from(child.children)) {
        if (!isHtmlEl(inner)) continue;
        if (isBookMark(inner)) continue;
        if (isManualPageBreak(inner)) {
          pushBreak();
          continue;
        }
        parts.push(inner.outerHTML);
      }
      continue;
    }

    if (isManualPageBreak(child)) {
      pushBreak();
      continue;
    }
    parts.push(child.outerHTML);
  }

  return parts.join('') || '<p></p>';
}

/**
 * Flatten page shells in an HTML string (GetContent / BeforeSetContent).
 * @param {string} html
 */
function serializeDocumentHtml(html) {
  if (!html) return html;
  if (
    !html.includes(PAGE_ATTR)
    && !html.includes(GAP_ATTR)
    && !html.includes('pdf-doc-page')
  ) {
    return html;
  }

  const wrap = document.createElement('div');
  wrap.innerHTML = html;
  /** @type {string[]} */
  const parts = [];
  const pushBreak = () => {
    parts.push('<div class="mce-pagebreak pdf-manual-page-break" data-mce-type="pagebreak"></div>');
  };

  for (const child of Array.from(wrap.childNodes)) {
    if (!isHtmlEl(child)) {
      const t = child.textContent || '';
      if (t.trim()) parts.push(`<p>${t}</p>`);
      continue;
    }
    if (isPageGap(child)) continue;
    if (isPageShell(child)) {
      for (const inner of Array.from(child.children)) {
        if (!isHtmlEl(inner)) continue;
        if (isManualPageBreak(inner)) {
          pushBreak();
          continue;
        }
        parts.push(inner.outerHTML);
      }
      continue;
    }
    if (isManualPageBreak(child)) {
      pushBreak();
      continue;
    }
    parts.push(child.outerHTML);
  }

  return parts.join('') || '<p></p>';
}

/**
 * @param {import('tinymce').Editor} editor
 * @param {ReturnType<typeof resolvePageBox>} box
 */
function applyPageChrome(editor, box) {
  const doc = editor.getDoc?.();
  if (!doc) return;

  let style = doc.getElementById('pdf-page-chrome');
  if (!style) {
    style = doc.createElement('style');
    style.id = 'pdf-page-chrome';
    doc.head.appendChild(style);
  }

  // Avoid rewriting identical CSS (causes style recalc).
  const next = `
    html { background: #c5cad3 !important; }
    body {
      margin: 28px auto 48px !important;
      padding: 0 !important;
      width: ${PAGE_W}px !important;
      max-width: ${PAGE_W}px !important;
      min-height: 0 !important;
      background: transparent !important;
      border: 0 !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
    }
    .pdf-doc-page {
      display: block !important;
      box-sizing: border-box !important;
      width: ${PAGE_W}px !important;
      height: ${PAGE_H}px !important;
      min-height: ${PAGE_H}px !important;
      max-height: ${PAGE_H}px !important;
      overflow: hidden !important;
      overflow-x: hidden !important;
      overflow-y: hidden !important;
      padding: ${box.top}px ${box.right}px ${box.bottom}px ${box.left}px !important;
      margin: 0 !important;
      background: #ffffff !important;
      border: 1px solid #b8bfc9 !important;
      box-shadow: 0 1px 3px rgba(15,23,42,.14), 0 10px 28px rgba(15,23,42,.12);
    }
    .pdf-doc-page img {
      max-width: 100% !important;
      max-height: ${Math.max(120, box.contentHeight - 8)}px !important;
      height: auto !important;
      object-fit: contain !important;
    }
    .pdf-doc-page table {
      max-width: 100% !important;
      width: 100% !important;
    }
    .pdf-doc-page-gap {
      display: block !important;
      box-sizing: border-box !important;
      width: ${PAGE_W}px !important;
      height: ${PAGE_GAP_PX}px !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      overflow: hidden !important;
      line-height: 0 !important;
      font-size: 0 !important;
      background: #c5cad3 !important;
      pointer-events: none !important;
      user-select: none !important;
    }
    .mce-pagebreak,
    .pdf-manual-page-break {
      display: block !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
      overflow: hidden !important;
      page-break-before: always;
    }
  `;
  if (style.textContent !== next) style.textContent = next;
}

const ZOOM_STEPS = [50, 75, 90, 100, 125, 150, 175, 200];

/**
 * Visual zoom only — A4 layout metrics stay in CSS pixels.
 * @param {import('tinymce').Editor} editor
 * @param {number} zoomPct
 */
function applyEditorZoom(editor, zoomPct) {
  const doc = editor.getDoc?.();
  const html = doc?.documentElement;
  const body = editor.getBody?.();
  if (!html || !body) return;
  const z = Math.max(0.4, Math.min(2.5, Number(zoomPct) / 100));
  // Chromium / Safari
  html.style.zoom = String(z);
  // Firefox fallback
  if (!('zoom' in html.style) || html.style.zoom === '') {
    html.style.zoom = '';
    body.style.transform = z === 1 ? '' : `scale(${z})`;
    body.style.transformOrigin = 'top center';
  } else {
    body.style.transform = '';
    body.style.transformOrigin = '';
  }
}

/**
 * Zoom so an A4 sheet fits the edit-area width (white page on gray desk).
 * @param {number} hostWidthPx
 */
function fitZoomForWidth(hostWidthPx) {
  const usable = Math.max(160, Number(hostWidthPx) - 32);
  let best = ZOOM_STEPS[0];
  for (const step of ZOOM_STEPS) {
    if (PAGE_W * (step / 100) <= usable) best = step;
  }
  return best;
}

/**
 * Word-like document editor for PDF documents.
 * Each sheet is a fixed-height page shell inside one TinyMCE instance (shared toolbar/undo).
 * @param {{
 *   value: string,
 *   onChange: (html: string) => void,
 *   disabled?: boolean,
 *   title?: string,
 *   margins?: { top?: number, right?: number, bottom?: number, left?: number },
 * }} props
 */
export function PdfRichDocumentEditor({
  value,
  onChange,
  disabled = false,
  title = 'Document',
  margins,
}) {
  const reactId = useId().replace(/:/g, '');
  const editorId = `pdf-doc-editor-${reactId}`;
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  const marginsRef = useRef(margins);
  const syncingRef = useRef(false);
  const emittingRef = useRef(false);
  const balanceTimerRef = useRef(0);
  const emitTimerRef = useRef(0);
  const chromeAppliedForRef = useRef('');
  const lastEmittedHtmlRef = useRef('');
  const pointerSelectingRef = useRef(false);
  const selectionLockUntilRef = useRef(0);
  const zoomRef = useRef(100);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [zoom, setZoom] = useState(100);
  const box = useMemo(() => resolvePageBox(margins), [margins]);
  onChangeRef.current = onChange;
  valueRef.current = value;
  marginsRef.current = margins;
  zoomRef.current = zoom;

  useEffect(() => {
    let cancelled = false;
    let editorInstance = null;
    setReady(false);
    setLoadError('');

    // Patch early — toolbar lives in the parent window, content in the iframe.
    const uninstallParentAppendPatch = installSafeAppendChildPatch(window);
    let uninstallFrameAppendPatch = () => {};

    const initialBox = resolvePageBox(marginsRef.current);

    const selecting = (editor) => isUserSelecting(
      editor,
      pointerSelectingRef.current,
      selectionLockUntilRef.current,
    );

    const emitHtml = (editor, immediate = false) => {
      const run = () => {
        if (!editor || editor.removed || cancelled) return;
        if (selecting(editor)) return;
        const html = serializeLiveDocument(editor);
        const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        const hasMedia = /<img\b/i.test(html) || /<table\b/i.test(html);
        const prev = lastEmittedHtmlRef.current || '';
        const prevText = prev.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        const parentText = String(valueRef.current || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        // Never push an empty editor state over a document that already has content.
        if (!hasMedia && text.length < 8 && (prevText.length >= 8 || parentText.length >= 8)) return;
        if (html === lastEmittedHtmlRef.current) return;
        lastEmittedHtmlRef.current = html;
        // Prevent value→setContent echo from wiping TinyMCE undo history.
        // Cleared in the value effect (and a safety timeout if value does not change).
        emittingRef.current = true;
        onChangeRef.current(html);
        window.setTimeout(() => {
          emittingRef.current = false;
        }, 100);
      };
      window.clearTimeout(emitTimerRef.current);
      if (immediate) run();
      else emitTimerRef.current = window.setTimeout(run, 280);
    };

    const runBalance = (editor) => {
      if (!editor || editor.removed || cancelled || syncingRef.current) return;
      if (selecting(editor)) return;
      syncingRef.current = true;
      // Track the live caret block — bookmarks break when Enter's new <p> is moved
      // to the next A4 sheet (caret would snap back to the previous paragraph).
      const caretBlock = getCaretBlock(editor);
      const caretEmpty = isHtmlEl(caretBlock)
        && !(caretBlock.textContent || '').replace(/\u00a0/g, ' ').trim();
      const bookmark = caretBlock ? null : captureSelection(editor);
      try {
        const boxNow = resolvePageBox(marginsRef.current);
        // Pagination must not create undo steps — otherwise Ctrl+Z fights the page packer.
        const ignore = editor.undoManager?.ignore?.bind(editor.undoManager);
        if (ignore) ignore(() => balanceOverflow(editor, boxNow));
        else balanceOverflow(editor, boxNow);
        if (selecting(editor)) return;
        if (isHtmlEl(caretBlock) && editor.getBody()?.contains(caretBlock)) {
          placeCaretInBlock(editor, caretBlock, { toStart: caretEmpty });
        } else {
          restoreSelection(editor, bookmark);
        }
      } finally {
        syncingRef.current = false;
      }
    };

    /** Immediate A4 reflow — used on Enter so the sheet never visibly grows. */
    const runBalanceNow = (editor) => {
      window.clearTimeout(balanceTimerRef.current);
      window.requestAnimationFrame(() => {
        if (!editor || editor.removed || cancelled || syncingRef.current) return;
        if (selecting(editor)) return;
        const boxNow = resolvePageBox(marginsRef.current);
        const caretBlock = getCaretBlock(editor);
        runBalance(editor);
        if (!selecting(editor)) {
          // If bookmark/path restore left caret on the previous sheet, jump to the
          // Enter paragraph that overflow moved onto the next page.
          if (isHtmlEl(caretBlock) && editor.getBody()?.contains(caretBlock)) {
            placeCaretInBlock(editor, caretBlock, { toStart: true });
          }
          followEnterParagraphToNextPage(editor, boxNow);
          ensureCaretOnWritablePage(editor, boxNow);
          emitHtml(editor);
        }
      });
    };

    const runFullRebuild = (editor) => {
      if (!editor || editor.removed || cancelled || syncingRef.current) return;
      syncingRef.current = true;
      const bookmark = captureSelection(editor);
      try {
        const boxNow = resolvePageBox(marginsRef.current);
        applyPageChrome(editor, boxNow);
        chromeAppliedForRef.current = JSON.stringify(boxNow.marginsMm);
        const ignore = editor.undoManager?.ignore?.bind(editor.undoManager);
        if (ignore) ignore(() => rebuildAllPages(editor, boxNow));
        else rebuildAllPages(editor, boxNow);
        restoreSelection(editor, bookmark);
      } finally {
        syncingRef.current = false;
      }
    };

    const scheduleBalance = (editor) => {
      window.clearTimeout(balanceTimerRef.current);
      balanceTimerRef.current = window.setTimeout(() => {
        window.requestAnimationFrame(() => {
          if (selecting(editor)) return;
          runBalance(editor);
          if (!selecting(editor)) emitHtml(editor);
        });
      }, 120);
    };

    const scheduleBalanceSoon = (editor) => {
      if (selecting(editor)) return;
      const body = editor.getBody?.();
      if (!body) return;
      // Always pull orphans off the gray desk, even when pages are not overflowing.
      const ignore = editor.undoManager?.ignore?.bind(editor.undoManager);
      if (ignore) ignore(() => rehomeStrayBodyNodes(editor));
      else rehomeStrayBodyNodes(editor);
      if (!selectionHostPage(editor)) constrainCaretToPages(editor);

      const pages = Array.from(body.querySelectorAll(`[${PAGE_ATTR}]`)).filter(
        (n) => isHtmlEl(n),
      );
      // Soft check — avoid rewriting page styles while the user may still be interacting.
      const needsReflow =
        pages.some((pg) => pageOverflows(pg, { relock: false }))
        || pages.some((pg, i) => {
          const next = pages[i + 1];
          return isHtmlEl(next) && pg.nextElementSibling === next;
        });
      if (needsReflow) scheduleBalance(editor);
      else emitHtml(editor);
    };

    const syncAfterUndoRedo = (editor) => {
      // TinyMCE already restored the snapshot — do not re-pack pages (that re-applies
      // overflow splits and makes Ctrl+Z look broken). Only lock chrome + sync React.
      window.setTimeout(() => {
        if (!editor || editor.removed || cancelled || syncingRef.current) return;
        const body = editor.getBody?.();
        if (body) {
          for (const pg of body.querySelectorAll(`[${PAGE_ATTR}]`)) {
            if (isHtmlEl(pg)) lockPageChrome(pg);
          }
        }
        emitHtml(editor, true);
      }, 0);
    };

    loadTinyMce()
      .then((tinymce) => {
        if (cancelled) return;

        return tinymce.init({
          selector: `#${editorId}`,
          ...TINYMCE_BASE,
          skin: 'oxide',
          content_css: false,
          elementpath: false,
          menubar: false,
          plugins:
            'advlist autolink lists link image table code preview searchreplace wordcount visualblocks fullscreen media charmap pagebreak insertdatetime',
          toolbar:
            'undo redo | blocks fontfamily fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link image table | insertpagebreak | removeformat | fullscreen',
          toolbar_mode: 'wrap',
          toolbar_sticky: true,
          font_family_formats:
            'Calibri=Calibri,Candara,Segoe,Segoe UI,Optima,Arial,sans-serif;' +
            'Arial=Arial,Helvetica,sans-serif;' +
            'Georgia=Georgia,serif;' +
            'Times New Roman=Times New Roman,Times,serif;' +
            'Verdana=Verdana,Geneva,sans-serif;' +
            'Courier New=Courier New,Courier,monospace',
          font_size_formats: '8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 24pt 28pt 36pt',
          block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Quote=blockquote',
          height: '100%',
          min_height: 320,
          resize: false,
          statusbar: true,
          highlight_on_focus: false,
          image_title: true,
          automatic_uploads: true,
          file_picker_types: 'image',
          images_file_types: 'jpeg,jpg,png,gif,webp',
          paste_data_images: true,
          browser_spellcheck: true,
          verify_html: false,
          contextmenu: 'link image table lists',
          // Keep page-shell <div>s from wiping nested p/table/img (extended div rules can reset children).
          valid_children:
            '+body[div|p|h1|h2|h3|h4|h5|h6|ul|ol|table|blockquote|pre|hr|img|figure|#text],'
            + '+div[*]',
          valid_elements: '*[*]',
          extended_valid_elements: '*[*]',
          valid_styles: {
            '*': 'font-family,font-size,font-weight,font-style,color,background,background-color,text-align,text-decoration,line-height,letter-spacing,white-space,margin,margin-top,margin-right,margin-bottom,margin-left,padding,padding-top,padding-right,padding-bottom,padding-left,width,max-width,min-width,height,max-height,min-height,overflow,overflow-x,overflow-y,border,border-collapse,border-spacing,vertical-align,object-fit,box-sizing,display,flex,visibility,opacity,position,top,left,right,bottom,z-index,pointer-events,user-select,box-shadow,transform,transform-origin',
          },
          content_style: `
            html {
              background: #c5cad3;
              height: auto;
              overflow-x: hidden;
            }
            body {
              font-family: Calibri, Candara, Segoe, "Segoe UI", Optima, Arial, sans-serif;
              font-size: 11pt;
              line-height: 1.5;
              color: #111827;
              width: ${PAGE_W}px !important;
              max-width: ${PAGE_W}px !important;
              margin: 28px auto 48px !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              background: transparent !important;
              border: 0 !important;
              box-shadow: none !important;
              overflow-x: hidden !important;
              min-height: 0 !important;
            }
            .pdf-doc-page {
              display: block !important;
              box-sizing: border-box !important;
              width: ${PAGE_W}px !important;
              height: ${PAGE_H}px !important;
              min-height: ${PAGE_H}px !important;
              max-height: ${PAGE_H}px !important;
              overflow: hidden !important;
              overflow-x: hidden !important;
              overflow-y: hidden !important;
              padding: ${initialBox.top}px ${initialBox.right}px ${initialBox.bottom}px ${initialBox.left}px !important;
              margin: 0 !important;
              background: #ffffff !important;
              border: 1px solid #b8bfc9 !important;
              box-shadow: 0 1px 3px rgba(15,23,42,.14), 0 10px 28px rgba(15,23,42,.12);
            }
            .pdf-doc-page-gap {
              display: block !important;
              height: ${PAGE_GAP_PX}px !important;
              max-height: ${PAGE_GAP_PX}px !important;
              background: #c5cad3 !important;
              overflow: hidden !important;
              line-height: 0 !important;
              font-size: 0 !important;
              user-select: none !important;
              pointer-events: none !important;
            }
            p { margin: 0 0 10pt; }
            h1 { font-size: 24pt; line-height: 1.25; margin: 0 0 14pt; font-weight: 700; }
            h2 { font-size: 16pt; line-height: 1.3; margin: 18pt 0 10pt; font-weight: 700; }
            h3 { font-size: 13pt; line-height: 1.35; margin: 14pt 0 8pt; font-weight: 700; }
            h4 { font-size: 12pt; margin: 12pt 0 6pt; font-weight: 700; }
            ul, ol { margin: 0 0 10pt; padding-left: 24pt; }
            li { margin: 0 0 4pt; }
            img { max-width: 100%; height: auto; object-fit: contain; }
            table { width: 100%; max-width: 100%; border-collapse: collapse; margin: 12pt 0; }
            td, th { border: 1px solid #9ca3af; padding: 6pt 8pt; vertical-align: top; }
            blockquote {
              margin: 12pt 0;
              padding: 6pt 14pt;
              border-left: 3px solid #9ca3af;
              color: #374151;
            }
            a { color: #1d4ed8; }
            hr { border: 0; border-top: 1px solid #cbd5e1; margin: 18pt 0; }
          `,
          images_upload_handler: (blobInfo) =>
            new Promise((resolve, reject) => {
              const blob = blobInfo.blob();
              if (blob.size > 25 * 1024 * 1024) {
                reject('Image must be 25 MB or smaller');
                return;
              }
              const reader = new FileReader();
              reader.onload = () => {
                const dataUrl = String(reader.result || '');
                if (!dataUrl.startsWith('data:image/')) {
                  resolve(dataUrl);
                  return;
                }
                const img = new Image();
                img.onload = () => {
                  // Match PDF export: content column × html2canvas scale (2).
                  const contentW = resolvePageBox(marginsRef.current).contentWidth;
                  const maxW = Math.max(320, Math.round(contentW * 2));
                  const scale = Math.min(1, maxW / img.width);
                  const w = Math.max(1, Math.round(img.width * scale));
                  const h = Math.max(1, Math.round(img.height * scale));
                  const canvas = document.createElement('canvas');
                  canvas.width = w;
                  canvas.height = h;
                  const ctx = canvas.getContext('2d');
                  const preferPng = blob.type.includes('png');
                  if (preferPng) {
                    ctx.clearRect(0, 0, w, h);
                    ctx.drawImage(img, 0, 0, w, h);
                    resolve(canvas.toDataURL('image/png'));
                  } else {
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, w, h);
                    ctx.drawImage(img, 0, 0, w, h);
                    resolve(canvas.toDataURL('image/jpeg', 0.82));
                  }
                };
                img.onerror = () => resolve(dataUrl);
                img.src = dataUrl;
              };
              reader.onerror = () => reject('Failed to read image');
              reader.readAsDataURL(blob);
            }),
          file_picker_callback: (callback, _value, meta) => {
            if (meta.filetype !== 'image') return;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = () => {
              const file = input.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = () => callback(String(reader.result || ''), { title: file.name });
              reader.readAsDataURL(file);
            };
            input.click();
          },
          setup: (editor) => {
            editorInstance = editor;
            editor.on('PreInit', () => {
              editor.schema.addValidElements('*[*]');
              editor.schema.addValidChildren('+div[*]');
              editor.schema.addValidChildren(
                '+body[div|p|h1|h2|h3|h4|h5|h6|ul|ol|table|blockquote|pre|hr|img|figure|#text]',
              );
            });
            editor.on('init', () => {
              uninstallFrameAppendPatch = installSafeAppendChildPatch(editor.getWin?.());
            });
            editor.on('GetContent', (e) => {
              // Prefer live DOM serialize — getContent can drop nested page content.
              e.content = serializeLiveDocument(editor);
            });
            editor.on('BeforeSetContent', (e) => {
              if (typeof e.content === 'string') {
                e.content = serializeDocumentHtml(e.content);
              }
            });
            editor.ui.registry.addButton('insertpagebreak', {
              tooltip: 'Insert page break',
              text: 'Page break',
              icon: 'page-break',
              onAction: () => {
                editor.insertContent(
                  '<div class="mce-pagebreak pdf-manual-page-break" data-mce-type="pagebreak"></div><p></p>',
                );
                window.setTimeout(() => {
                  runFullRebuild(editor);
                  emitHtml(editor, true);
                }, 0);
              },
            });

            // Typing: debounce parent updates; only balance pages when overflowing.
            let undoing = false;
            const scoopDeskStrays = () => {
              if (!editor || editor.removed || syncingRef.current) return;
              const ignore = editor.undoManager?.ignore?.bind(editor.undoManager);
              if (ignore) ignore(() => rehomeStrayBodyNodes(editor));
              else rehomeStrayBodyNodes(editor);
              if (!selectionHostPage(editor)) constrainCaretToPages(editor);
            };
            const onPointerSelectStart = (e) => {
              pointerSelectingRef.current = true;
              selectionLockUntilRef.current = Date.now() + 800;
              // Clicks on the gray desk / page gap must not leave a caret between sheets.
              try {
                const raw = e?.target;
                const el = raw?.nodeType === 3 ? raw.parentElement : raw;
                const inPage = isHtmlEl(el) && Boolean(el.closest?.(`[${PAGE_ATTR}]`));
                const inGap = isHtmlEl(el) && (isPageGap(el) || Boolean(el.closest?.(`[${GAP_ATTR}]`)));
                if (inPage && !inGap) return;
                const body = editor.getBody?.();
                if (!body) return;
                e?.preventDefault?.();
                e?.stopPropagation?.();
                const clientY = typeof e?.clientY === 'number'
                  ? e.clientY
                  : (typeof e?.originalEvent?.clientY === 'number' ? e.originalEvent.clientY : undefined);
                scoopDeskStrays();
                constrainCaretToPages(editor, { clientY, force: true });
                editor.focus?.();
              } catch {
                // ignore
              }
            };
            const onPointerSelectEnd = () => {
              if (!pointerSelectingRef.current) return;
              pointerSelectingRef.current = false;
              // Keep a short lock so NodeChange / toolbar updates after mouseup
              // cannot reflow pages and blow up selection (HierarchyRequestError).
              selectionLockUntilRef.current = Date.now() + 800;
              // Desk click only — never disturb an in-progress text selection.
              try {
                if (syncingRef.current || undoing || !editor.selection.isCollapsed()) return;
                if (!selectionHostPage(editor)) scoopDeskStrays();
              } catch {
                // ignore
              }
            };
            editor.on('mousedown', onPointerSelectStart);
            editor.on('mouseup', onPointerSelectEnd);
            editor.getDoc()?.addEventListener?.('mouseup', onPointerSelectEnd);
            editor.getWin()?.addEventListener?.('blur', onPointerSelectEnd);
            // TinyMCE format UI (parent toolbar) can throw HierarchyRequestError while
            // selecting headings inside page shells — swallow during selection.
            const swallowSelectionDomError = (ev) => {
              if (!selecting(editor)) return;
              const msg = String(ev?.message || ev?.error?.message || '');
              if (/HierarchyRequestError|appendChild/i.test(msg)) {
                ev.preventDefault?.();
                ev.stopImmediatePropagation?.();
              }
            };
            window.addEventListener('error', swallowSelectionDomError, true);
            editor.getWin()?.addEventListener?.('error', swallowSelectionDomError, true);
            const onSelectionChange = () => {
              try {
                if (!editor.selection.isCollapsed()) {
                  selectionLockUntilRef.current = Date.now() + 800;
                }
              } catch {
                selectionLockUntilRef.current = Date.now() + 800;
              }
            };
            editor.getDoc()?.addEventListener?.('selectionchange', onSelectionChange);
            editor.on('remove', () => {
              window.removeEventListener('error', swallowSelectionDomError, true);
              editor.getWin()?.removeEventListener?.('error', swallowSelectionDomError, true);
              editor.getDoc()?.removeEventListener?.('selectionchange', onSelectionChange);
            });

            editor.on('input', () => {
              if (syncingRef.current || undoing) return;
              if (selecting(editor)) return;
              scoopDeskStrays();
              scheduleBalanceSoon(editor);
            });
            editor.on('change', () => {
              if (syncingRef.current || undoing) return;
              if (selecting(editor)) return;
              scoopDeskStrays();
              scheduleBalanceSoon(editor);
            });
            editor.on('beforeinput', (e) => {
              if (syncingRef.current || undoing) return;
              if (selectionHostPage(editor)) return;
              // Block typing into the gray desk; rehome then place caret on a page.
              e?.preventDefault?.();
              scoopDeskStrays();
              constrainCaretToPages(editor, { force: true });
            });
            // Enter at the bottom of A4 must jump to the next sheet immediately (never grow the page).
            editor.on('keydown', (e) => {
              if (syncingRef.current || undoing) return;
              if (selecting(editor)) return;
              // Printable keys / Backspace while caret is off-page → snap back first.
              if (!selectionHostPage(editor) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                scoopDeskStrays();
                constrainCaretToPages(editor, { force: true });
              }
              const isEnter = e.key === 'Enter' || e.keyCode === 13;
              if (!isEnter || e.shiftKey) return;

              const boxNow = resolvePageBox(marginsRef.current);
              const body = editor.getBody?.();
              let page = null;
              try {
                page = editor.selection.getNode()?.closest?.(`[${PAGE_ATTR}]`) || null;
              } catch {
                page = null;
              }
              // If the current A4 sheet is already full (or almost), start the new paragraph on the next sheet.
              if (
                body
                && isHtmlEl(page)
                && isCaretAtEndOfPage(editor, page)
                && (pageNearBottom(page) || pageRoomBelowLastBlock(page) <= 40)
              ) {
                e.preventDefault();
                e.stopPropagation();
                const nextPage = getOrCreateNextPage(page, body, boxNow);
                const p = editor.getDoc().createElement('p');
                p.innerHTML = '<br data-mce-bogus="1">';
                safeAppendChild(nextPage, p);
                lockPageChrome(page);
                lockPageChrome(nextPage);
                try {
                  editor.selection.setCursorLocation(p, 0);
                  editor.nodeChanged?.();
                  // Scroll to the new caret on the next sheet (top of content), not the whole page shell.
                  scrollCaretIntoView(editor, { prefer: 'start' });
                } catch {
                  // ignore
                }
                emitHtml(editor, true);
                return false;
              }

              window.setTimeout(() => {
                if (!editor.removed && !cancelled && !undoing) runBalanceNow(editor);
              }, 0);
            });
            editor.on('BeforeUndo BeforeRedo', () => {
              undoing = true;
            });
            editor.on('undo redo', () => {
              undoing = true;
              if (!syncingRef.current) syncAfterUndoRedo(editor);
              window.setTimeout(() => {
                undoing = false;
              }, 80);
            });
            // Do NOT full-rebuild on SetContent — undo/redo uses setContent and a rebuild
            // would fight the undo stack and re-flow overflow immediately after Ctrl+Z.
            editor.on('PastePostProcess ObjectResized', () => {
              if (undoing || selecting(editor)) return;
              window.setTimeout(() => {
                if (!editor.removed && !cancelled && !undoing && !selecting(editor)) {
                  runBalance(editor);
                  emitHtml(editor, true);
                }
              }, 50);
            });
            // Selection / toolbar NodeChange must NOT reflow pages — that races with
            // heading selection and can throw HierarchyRequestError via appendChild.
            editor.on('NodeChange', () => {
              if (syncingRef.current || undoing || selecting(editor)) return;
              // Only soft-rehome desk orphans; never runBalance from caret moves.
              try {
                if (!selectionHostPage(editor)) scoopDeskStrays();
              } catch {
                // ignore
              }
            });
            editor.getBody?.()?.addEventListener?.(
              'load',
              (ev) => {
                const t = /** @type {HTMLElement} */ (ev.target);
                if (t?.nodeName === 'IMG' && !syncingRef.current && !selecting(editor)) {
                  scheduleBalance(editor);
                }
              },
              true,
            );
            editor.getDoc()?.addEventListener?.(
              'wheel',
              (ev) => {
                if (!(ev.ctrlKey || ev.metaKey)) return;
                ev.preventDefault();
                const dir = ev.deltaY > 0 ? -1 : 1;
                const cur = zoomRef.current;
                let idx = 0;
                let best = Math.abs(ZOOM_STEPS[0] - cur);
                for (let i = 1; i < ZOOM_STEPS.length; i += 1) {
                  const d = Math.abs(ZOOM_STEPS[i] - cur);
                  if (d < best) {
                    best = d;
                    idx = i;
                  }
                }
                const next = ZOOM_STEPS[Math.max(0, Math.min(ZOOM_STEPS.length - 1, idx + dir))];
                setZoom(next);
              },
              { passive: false },
            );
          },
          init_instance_callback: (editor) => {
            if (cancelled) {
              editor.remove();
              return;
            }
            const syncFrameToHost = () => {
              const container = editor.getContainer?.();
              if (!container) return;
              const isFs = Boolean(editor.plugins?.fullscreen?.isFullscreen?.())
                || container.classList.contains('tox-fullscreen')
                || document.body.classList.contains('tox-fullscreen');
              if (isFs) {
                // Do NOT reparent the container — moving it reloads the iframe and wipes pages.
                container.style.removeProperty('max-height');
                container.style.removeProperty('min-height');
                container.style.removeProperty('height');
                container.style.removeProperty('width');
                container.style.removeProperty('top');
                container.style.removeProperty('left');
                container.style.setProperty('position', 'fixed', 'important');
                container.style.setProperty('inset', '0', 'important');
                container.style.setProperty('width', '100vw', 'important');
                container.style.setProperty('height', '100vh', 'important');
                container.style.setProperty('max-width', 'none', 'important');
                container.style.setProperty('max-height', 'none', 'important');
                container.style.setProperty('min-height', '100vh', 'important');
                container.style.setProperty('z-index', '2147483000', 'important');
                container.style.setProperty('pointer-events', 'auto', 'important');
                window.setTimeout(() => {
                  try {
                    editor.fire?.('ResizeEditor');
                  } catch {
                    // ignore
                  }
                }, 0);
                return;
              }
              const host = document.querySelector(`#${editorId}`)?.closest?.('.pdf-word-editor-host')
                || container.parentElement;
              if (!host) return;
              container.style.removeProperty('inset');
              container.style.removeProperty('z-index');
              container.style.removeProperty('position');
              container.style.removeProperty('max-width');
              container.style.removeProperty('pointer-events');
              const h = Math.floor(host.getBoundingClientRect().height);
              if (h < 160) return;
              container.style.height = `${h}px`;
              container.style.maxHeight = `${h}px`;
              container.style.minHeight = '0';
              container.style.width = '';
              container.style.top = '';
              container.style.left = '';
            };
            editor.on('FullscreenStateChanged', () => {
              window.requestAnimationFrame(() => {
                syncFrameToHost();
                window.setTimeout(syncFrameToHost, 30);
                window.setTimeout(syncFrameToHost, 120);
              });
            });
            const incoming = valueRef.current || '<p></p>';
            syncingRef.current = true;
            try {
              // Raw set avoids TinyMCE cleanup wiping imported markup before we pack pages.
              editor.setContent(incoming, { format: 'raw' });
              runFullRebuild(editor);
              let packed = serializeLiveDocument(editor);
              const packedText = packed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
              const incomingText = incoming.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
              if (incomingText.length > 20 && packedText.length < 8) {
                // Last resort: flat HTML inside one A4 shell (iframe document only).
                const body = editor.getBody();
                if (body) {
                  const boxNow = resolvePageBox(marginsRef.current);
                  const page = createPageShell(boxNow, body.ownerDocument);
                  page.innerHTML = serializeDocumentHtml(incoming) || incoming;
                  body.innerHTML = page.outerHTML;
                  packed = serializeLiveDocument(editor);
                }
              }
              lastEmittedHtmlRef.current = packed;
              // Packing must not leave junk on the undo stack.
              editor.undoManager?.clear?.();
              editor.undoManager?.add?.();
            } finally {
              syncingRef.current = false;
            }
            // One delayed pass after images/fonts settle — not a loop.
            window.setTimeout(() => {
              if (!cancelled && !editor.removed) {
                runBalance(editor);
                const again = serializeLiveDocument(editor);
                const againText = again.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                const parentText = String(valueRef.current || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
                if (againText.length >= 8 || /<img\b/i.test(again)) {
                  lastEmittedHtmlRef.current = again;
                } else if (parentText.length >= 8) {
                  // Balance wiped content — restore from React value, do not emit empty.
                  syncingRef.current = true;
                  try {
                    editor.setContent(valueRef.current || '<p></p>', { format: 'raw' });
                    runFullRebuild(editor);
                    lastEmittedHtmlRef.current = serializeLiveDocument(editor);
                  } finally {
                    syncingRef.current = false;
                  }
                }
                editor.undoManager?.clear?.();
                editor.undoManager?.add?.();
              }
            }, 400);
            if (disabled) editor.mode.set('readonly');
            syncFrameToHost();
            const hostForZoom = document.querySelector(`#${editorId}`)?.closest?.('.pdf-word-editor-host')
              || editor.getContainer()?.parentElement;
            const fit = fitZoomForWidth(hostForZoom?.getBoundingClientRect?.().width || 0);
            if (fit < zoomRef.current) setZoom(fit);
            applyEditorZoom(editor, Math.min(zoomRef.current, fit));
            window.requestAnimationFrame(syncFrameToHost);
            const hostEl = editor.getContainer()?.parentElement;
            if (hostEl && typeof ResizeObserver !== 'undefined') {
              const ro = new ResizeObserver(() => syncFrameToHost());
              ro.observe(hostEl);
              editor.on('remove', () => ro.disconnect());
            }
            window.addEventListener('resize', syncFrameToHost);
            editor.on('remove', () => window.removeEventListener('resize', syncFrameToHost));
            setReady(true);
          },
        });
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(err?.message || 'Could not start the document editor');
        }
      });

    return () => {
      cancelled = true;
      setReady(false);
      window.clearTimeout(balanceTimerRef.current);
      window.clearTimeout(emitTimerRef.current);
      try {
        uninstallFrameAppendPatch();
      } catch {
        // ignore
      }
      try {
        uninstallParentAppendPatch();
      } catch {
        // ignore
      }
      if (editorInstance) {
        editorInstance.remove();
      } else {
        window.tinymce?.remove(`#${editorId}`);
      }
    };
  }, [editorId]);

  useEffect(() => {
    const editor = window.tinymce?.get(editorId);
    if (!editor || !ready) return;
    const key = JSON.stringify(box.marginsMm);
    if (chromeAppliedForRef.current === key) {
      applyPageChrome(editor, box);
      return;
    }
    const bookmark = captureSelection(editor);
    syncingRef.current = true;
    try {
      applyPageChrome(editor, box);
      chromeAppliedForRef.current = key;
      const ignore = editor.undoManager?.ignore?.bind(editor.undoManager);
      if (ignore) ignore(() => rebuildAllPages(editor, box));
      else rebuildAllPages(editor, box);
      restoreSelection(editor, bookmark);
    } finally {
      syncingRef.current = false;
    }
  }, [box, editorId, ready]);

  useEffect(() => {
    const editor = window.tinymce?.get(editorId);
    if (!editor || !ready) return;
    applyEditorZoom(editor, zoom);
  }, [zoom, editorId, ready]);

  useEffect(() => {
    const editor = window.tinymce?.get(editorId);
    if (!editor || !ready) return;
    // Echo from our own onChange — never setContent (that destroys undo history).
    if (emittingRef.current) {
      lastEmittedHtmlRef.current = value || '';
      emittingRef.current = false;
      return;
    }
    const next = value || '';
    if (next === lastEmittedHtmlRef.current) return;
    const current = serializeLiveDocument(editor);
    const currentText = current.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const nextText = String(next || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    // Never let an empty React value wipe a populated editor (bad emit / race).
    if (nextText.length < 8 && currentText.length >= 8 && !/<img\b/i.test(current)) {
      return;
    }
    if (current === next) {
      lastEmittedHtmlRef.current = next;
      return;
    }
    syncingRef.current = true;
    try {
      editor.setContent(next || '<p></p>', { format: 'raw' });
      const boxNow = resolvePageBox(marginsRef.current);
      applyPageChrome(editor, boxNow);
      const ignore = editor.undoManager?.ignore?.bind(editor.undoManager);
      if (ignore) ignore(() => rebuildAllPages(editor, boxNow));
      else rebuildAllPages(editor, boxNow);
      let packed = serializeLiveDocument(editor);
      const packedText = packed.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
      if (nextText.length > 20 && packedText.length < 8) {
        const body = editor.getBody();
        if (body) {
          const page = createPageShell(boxNow, body.ownerDocument);
          page.innerHTML = serializeDocumentHtml(next) || next;
          body.innerHTML = page.outerHTML;
        }
      }
      lastEmittedHtmlRef.current = serializeLiveDocument(editor);
      // External loads should not leave a polluted undo stack from packing.
      editor.undoManager?.clear?.();
      editor.undoManager?.add?.();
    } finally {
      syncingRef.current = false;
    }
  }, [value, editorId, ready]);

  useEffect(() => {
    const editor = window.tinymce?.get(editorId);
    if (!editor || !ready) return;
    editor.mode.set(disabled ? 'readonly' : 'design');
  }, [disabled, editorId, ready]);

  return (
    <div className={`pdf-word-shell${ready ? ' is-ready' : ''}${disabled ? ' is-disabled' : ''}`}>
      <div className="pdf-word-titlebar">
        <div className="pdf-word-titlebar-dot" />
        <div className="pdf-word-titlebar-dot" />
        <div className="pdf-word-titlebar-dot" />
        <span className="pdf-word-titlebar-name">{title || 'Document'}.docx</span>
        <div className="pdf-word-zoom" role="group" aria-label="Zoom">
          <button
            type="button"
            className="pdf-word-zoom-btn"
            disabled={zoom <= ZOOM_STEPS[0]}
            onClick={() => {
              const idx = ZOOM_STEPS.reduce(
                (best, step, i) => (Math.abs(step - zoom) < Math.abs(ZOOM_STEPS[best] - zoom) ? i : best),
                0,
              );
              setZoom(ZOOM_STEPS[Math.max(0, idx - 1)]);
            }}
            aria-label="Zoom out"
          >
            −
          </button>
          <SearchableSelect
            className="pdf-word-zoom-select"
            value={ZOOM_STEPS.includes(zoom) ? zoom : 100}
            onChange={(e) => setZoom(Number(e.target.value))}
            aria-label="Zoom level"
          >
            {ZOOM_STEPS.map((step) => (
              <option key={step} value={step}>
                {step}%
              </option>
            ))}
          </SearchableSelect>
          <button
            type="button"
            className="pdf-word-zoom-btn"
            disabled={zoom >= ZOOM_STEPS[ZOOM_STEPS.length - 1]}
            onClick={() => {
              const idx = ZOOM_STEPS.reduce(
                (best, step, i) => (Math.abs(step - zoom) < Math.abs(ZOOM_STEPS[best] - zoom) ? i : best),
                0,
              );
              setZoom(ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, idx + 1)]);
            }}
            aria-label="Zoom in"
          >
            +
          </button>
        </div>
      </div>

      {!ready && !loadError ? (
        <div className="pdf-word-loading" aria-live="polite">
          <div className="pdf-word-loading-card">
            <div className="pdf-word-loading-spinner" />
            <p>Opening document editor…</p>
          </div>
        </div>
      ) : null}

      {loadError ? (
        <div className="pdf-word-error">
          <strong>Editor failed to load</strong>
          <p>{loadError}</p>
          <p className="muted small">TinyMCE should be available at /tinymce from frontend/public/tinymce.</p>
        </div>
      ) : null}

      <div className="pdf-word-editor-host" hidden={Boolean(loadError)}>
        <textarea
          id={editorId}
          className="pdf-word-textarea-seed"
          defaultValue={value || ''}
          aria-hidden="true"
          tabIndex={-1}
        />
      </div>
    </div>
  );
}
